import { useState, useEffect, memo, useCallback } from 'react';
import {
    Box,
    Button,
    Flex,
    FormLabel,
    Input,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Text,
    VStack
} from '@chakra-ui/react';
import '../../assets/css/offerLetter/style.css'
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { defaultSalaryComponents } from 'services/Recruit/data/panel.Data';

const SalaryStructure = ({ totalSalary, value = [], permission, onChange, isOverviewMode, touched, setFieldTouched, setFieldValue, errors }) => {
    const [components, setComponents] = useState(value);
    const [lastCalculatedSalary, setLastCalculatedSalary] = useState(null);

    useEffect(() => {
        if (onChange) {
            onChange(components);
        }
    }, [components]);

    // Memoize calculateAmount to avoid recreating on every render
    const calculateAmount = useCallback((percentage) => {
        const salaryValue = Number(totalSalary);
        if (!totalSalary || isNaN(salaryValue) || salaryValue <= 0) {
            return 0;
        }
        return Math.round((salaryValue * percentage) / 100);
    }, [totalSalary]);

    const handlePercentageChange = (index, newPercentage) => {
        // 1️⃣ Remove non-numeric except dot
        let value = newPercentage.replace(/[^\d.]/g, "");

        // 2️⃣ Prevent more than one decimal
        const parts = value.split(".");
        if (parts.length > 2) value = parts[0] + "." + parts[1];

        // ⭐ NEW RULE — allow max 2 digits after decimal ⭐
        if (parts.length === 2 && parts[1].length > 2) {
            value = parts[0] + "." + parts[1].slice(0, 2);
        }

        // 4️⃣ Fix leading zero rules
        if (/^0\d/.test(value)) {
            value = value.replace(/^0+/, "");
            if (value === "") value = "0"; // keep at least one zero
        }

        // 5️⃣ Convert to number
        let pct = Number(value);

        // 6️⃣ Prevent anything above 100
        if (pct > 100) {
            return;
        }

        // 7️⃣ Update state
        const updated = [...components];
        updated[index].percentage = pct;
        updated[index].amount = calculateAmount(pct);
        setComponents(updated);

        // 8️⃣ Update Formik
        setFieldTouched(`salaryStructure.${index}.percentage`, true);
        setFieldValue(`salaryStructure.${index}.percentage`, pct);
        setFieldValue(`salaryStructure.${index}.amount`, updated[index].amount);
    };



    const handleComponentNameChange = (index, newName) => {
        const updated = [...components];
        updated[index].componentName = newName;  // FIXED
        setComponents(updated);

        setFieldTouched(`salaryStructure.${index}.componentName`, true);
        setFieldValue(`salaryStructure.${index}.componentName`, newName);
    };
    // Function to recalculate amounts - called when user finishes typing
    const recalculateAmounts = useCallback(() => {
        if (!value || value.length === 0) {
            setComponents(value || []);
            return;
        }

        const salaryValue = Number(totalSalary);
        if (!totalSalary || isNaN(salaryValue) || salaryValue <= 0) {
            // Keep components but set amounts to 0
            const updated = value.map((comp) => ({ ...comp, amount: 0 }));
            setComponents(updated);
            updated.forEach((comp, index) => {
                setFieldValue(`salaryStructure.${index}.amount`, 0, false);
            });
            setLastCalculatedSalary(totalSalary);
            return;
        }

        // Only recalculate if salary actually changed
        if (lastCalculatedSalary === salaryValue) {
            return;
        }

        const updated = value.map((comp) => {
            const newAmount = calculateAmount(comp.percentage);
            return { ...comp, amount: newAmount };
        });

        // Batch update all amounts at once without triggering validation
        updated.forEach((comp, index) => {
            setFieldValue(`salaryStructure.${index}.amount`, comp.amount, false);
        });

        setComponents(updated);
        setLastCalculatedSalary(salaryValue);
    }, [value, totalSalary, calculateAmount, setFieldValue, lastCalculatedSalary]);

    // Update amounts when totalSalary changes, but debounce heavily to avoid lag
    useEffect(() => {
        // Only update if totalSalary is a valid number and we have components
        if (!value || value.length === 0) {
            setComponents(value || []);
            return;
        }

        // Don't calculate if totalSalary is empty, 0, or invalid
        const salaryValue = Number(totalSalary);
        if (!totalSalary || isNaN(salaryValue) || salaryValue <= 0) {
            // Keep components but set amounts to 0
            const updated = value.map((comp) => ({ ...comp, amount: 0 }));
            setComponents(updated);
            setLastCalculatedSalary(totalSalary);
            return;
        }

        // Debounce heavily - wait for user to stop typing (1000ms delay)
        // This allows user to finish entering gross salary before calculations start
        const timeoutId = setTimeout(() => {
            recalculateAmounts();
        }, 1000); // Longer delay to let user finish typing gross salary

        return () => clearTimeout(timeoutId);
    }, [value, totalSalary, recalculateAmounts]);


    const addComponent = () => {
        const newComp = { componentName: "", percentage: 0, amount: 0 };

        setFieldTouched(`salaryStructure`, true);
        // 1️⃣ Update local state
        const updated = [...components, newComp];
        setComponents(updated);

        // 2️⃣ Update Formik array itself
        setFieldValue("salaryStructure", updated);

        // 3️⃣ Register new fields in touched (optional but ensures errors show instantly)
        const newIndex = updated.length - 1;
        setFieldTouched(`salaryStructure.${newIndex}.componentName`, true);
        setFieldTouched(`salaryStructure.${newIndex}.percentage`, true);
        // setFieldTouched(`salaryStructure.${newIndex}.amount`, true);
    };


    const removeComponent = (index) => {
        // 1️⃣ Remove from local state
        const updated = components.filter((_, i) => i !== index);
        setComponents(updated);

        // 2️⃣ Remove from Formik values
        setFieldValue(
            "salaryStructure",
            updated
        );

        // 3️⃣ Remove touched for that index
        if (touched.salaryStructure) {
            const updatedTouched = touched.salaryStructure.filter((_, i) => i !== index);
            setFieldTouched("salaryStructure", updatedTouched);
        }

        // 4️⃣ Remove errors for that index (if present)
        if (errors?.salaryStructure) {
            const updatedErrors = errors.salaryStructure.filter((_, i) => i !== index);
            setFieldValue("salaryStructureErrors", updatedErrors); // fallback if your Formik needs it
        }
    };


    const totalPercentage = components.reduce((sum, c) => sum + (c.percentage || 0), 0);

    return (
        <VStack spacing={4} align="stretch" className='salary-structure' maxW="800px">

            <VStack gap={0} align="stretch" p={4} border="1px solid"
                borderColor="#CFD6DC"
                borderRadius="8px">
                <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                        <FormLabel mb={0}>Salary Breakdown</FormLabel>
                        <Text fontSize="12px" color="gray.600" mt={1}>
                            Total: <Text as="span" fontWeight="600" color={totalPercentage !== 100 ? 'red.500' : 'green.500'}>
                                {totalPercentage}%
                            </Text>
                        </Text>
                    </Box>
                    <Button
                        disabled={!permission?.update || isOverviewMode}
                        leftIcon={<AddIcon />}
                        onClick={addComponent}
                        _disabled={{
                            cursor: "not-allowed",
                            opacity: 0.5,
                        }}
                        className="backBtn"
                        size="sm"
                    >
                        Add Component
                    </Button>
                </Flex>

                <Box overflowX="auto" maxHeight={'20rem'}>
                    <Table size="sm" variant="simple">
                        <Tbody overflow={true}>
                            {components.map((comp, index) => {
                                return (
                                    <Tr key={index}>
                                        <Td pl={3} pr={3} pt={3} pb={3} w={'auto'} minW={'200px'}>
                                            <Input
                                                value={comp.componentName || comp.component || ''}
                                                disabled={!permission?.update || isOverviewMode}
                                                onChange={(e) => handleComponentNameChange(index, e.target.value)}
                                                placeholder="Component name"
                                                size="sm"
                                                borderColor={
                                                    touched.salaryStructure
                                                        ?.[index]?.componentName && errors?.[index]?.componentName
                                                        ? "red.500!important"
                                                        : "#CFD6DC!important"
                                                }
                                                _focus={{
                                                    boxShadow: comp.componentName ? '0 0 0 1px #3C2FC0' : "none",
                                                }}
                                                _focusVisible={{
                                                    boxShadow: comp.componentName ? '0 0 0 1px #3C2FC0' : "none",
                                                }}
                                            />
                                        </Td>
                                        <Td isNumeric w={'auto'} minW={'120px'} pl={3} pr={3} pt={3} pb={3}>
                                            <Flex alignItems={'center'} gap={2}>
                                                <Input
                                                    type="number"
                                                    disabled={!permission?.update || isOverviewMode}
                                                    value={
                                                        comp.percentage !== "" && comp.percentage !== null
                                                            ? String(comp.percentage).replace(/^0+(?=\d)/, "")  // remove leading zeros only when followed by a digit
                                                            : ""
                                                    }
                                                    onChange={(e) => handlePercentageChange(index, e.target.value)}
                                                    placeholder="0"
                                                    size="sm"
                                                    w={'70px'}
                                                    borderColor={
                                                        touched.salaryStructure
                                                            ?.[index]?.percentage && errors?.[index]?.percentage
                                                            ? "red.500!important"
                                                            : "#CFD6DC!important"
                                                    }
                                                />
                                                <Box as="span" fontSize={'14px'} fontWeight={'400'} className='roboto'>
                                                    %
                                                </Box>
                                            </Flex>
                                        </Td>
                                        <Td isNumeric w={'auto'} minW={'150px'} pl={3} pr={3} pt={3} pb={3}>
                                            <Text fontWeight="400" fontSize={'14px'} className='roboto'>
                                                <Box as="span" mr={2} className='roboto'>
                                                    Amount:
                                                </Box>
                                                {calculateAmount(comp.percentage).toLocaleString('en-IN')}
                                            </Text>
                                        </Td>
                                        {components.length !== 1 && <Td w={'auto'} pl={2} pr={2} pt={3} pb={3}>
                                            <IconButton
                                                size="sm"
                                                disabled={!permission?.update || isOverviewMode}
                                                icon={<DeleteIcon />}
                                                _disabled={{
                                                    cursor: "not-allowed",
                                                    opacity: 0.5,
                                                }}
                                                onClick={() => removeComponent(index)}
                                                colorScheme="red"
                                                variant="ghost"
                                            />
                                        </Td>}
                                    </Tr>
                                )
                            })}
                        </Tbody>
                        <Tbody>
                            <Tr
                                bg="gray.50"
                                _hover={{ bg: "gray.50", color: "inherit" }}
                            >
                                <Td pl={3} pr={3} pt={2} pb={2}>
                                    <Text fontWeight="600" fontSize="14px" className="roboto">
                                        Total
                                    </Text>
                                </Td>
                                <Td isNumeric pl={3} pr={3} pt={2} pb={2}>
                                    <Text fontWeight="600" fontSize="14px" className="roboto">
                                        {totalPercentage} %
                                    </Text>
                                </Td>
                                <Td isNumeric pl={3} pr={3} pt={2} pb={2}>
                                    <Text fontWeight="600" fontSize="14px" className="roboto">
                                        {components
                                            .reduce((sum, c) => sum + calculateAmount(c.percentage), 0)
                                            .toLocaleString('en-IN')}
                                    </Text>
                                </Td>
                                <Td />
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>
                {totalPercentage !== 100 &&
                    <Text
                        mt={3}
                        mb={0}
                        fontWeight="400!important"
                        fontSize="14px"
                        borderRadius={'4px'}
                        bg={'#f7b8b8'}
                        color={'#000'}
                        p={'8px'}
                        textAlign={'left'}
                        className="roboto"  >
                        Total percentage must equal to 100%
                    </Text>
                }
            </VStack>
        </VStack>
    );
};

export default memo(SalaryStructure);